import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useLanguage } from '@/state/language/LanguageProvider';
import { toast } from 'react-hot-toast';
import {
  AE_BTN_PRIMARY,
  AE_BTN_SECONDARY,
  AE_CARD,
  AE_MUTED,
  AE_SECTION_TITLE,
  AE_TEXTAREA,
} from '@/components/assessment-engine/layout/aeTheme';
import { assessmentEngineService } from '@/services/assessmentEngineService';
import { parseInviteEmailList } from '../campaignSetupHelpers';

interface CampaignAddInvitesModalProps {
  campaignId: string;
  distributionMode: 'public' | 'invite_only';
  onClose: () => void;
  onAdded: () => void;
}

/** Modal to paste and bulk-add campaign invite emails. */
export const CampaignAddInvitesModal: React.FC<CampaignAddInvitesModalProps> = ({
  campaignId,
  distributionMode,
  onClose,
  onAdded,
}) => {
  const { t } = useLanguage();
  const [inviteText, setInviteText] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [onClose]);

  const submit = async () => {
    const emails = parseInviteEmailList(inviteText);
    if (!emails.length) {
      toast.error(t('assessmentEngine.hub.addInvitesEmpty'));
      return;
    }
    setBusy(true);
    try {
      const result = await assessmentEngineService.bulkInvites(campaignId, emails);
      if (result.emails_deferred) {
        toast.success(t('assessmentEngine.hub.invitesAddedPendingOpen', { count: result.inserted }));
      } else {
        toast.success(t('assessmentEngine.hub.invitesAdded', { count: result.inserted }));
      }
      onAdded();
      onClose();
    } catch {
      toast.error(t('assessmentEngine.hub.invitesFailed'));
    } finally {
      setBusy(false);
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="ae-add-invites-title"
      onClick={onClose}
    >
      <div
        className={`${AE_CARD} w-full max-w-lg space-y-4`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-2">
          <h2 id="ae-add-invites-title" className={AE_SECTION_TITLE}>
            {t('assessmentEngine.hub.addInvitesModalTitle')}
          </h2>
          <button
            type="button"
            className="rounded p-1 text-[var(--color-surface-text)] hover:bg-[var(--color-hover-bg)]"
            onClick={onClose}
            aria-label={t('assessmentEngine.preview.close')}
          >
            <XMarkIcon className="h-5 w-5" aria-hidden />
          </button>
        </div>
        <p className={AE_MUTED}>
          {distributionMode === 'invite_only'
            ? t('assessmentEngine.hub.inviteEmailsHintInviteOnly')
            : t('assessmentEngine.hub.inviteEmailsHintPublic')}
        </p>
        <textarea
          className={AE_TEXTAREA}
          rows={5}
          placeholder="one@example.com, two@example.com"
          value={inviteText}
          onChange={(e) => setInviteText(e.target.value)}
          autoFocus
        />
        <div className="flex justify-end gap-2">
          <button type="button" className={AE_BTN_SECONDARY} disabled={busy} onClick={onClose}>
            {t('assessmentEngine.campaign.cancel')}
          </button>
          <button type="button" className={AE_BTN_PRIMARY} disabled={busy} onClick={() => void submit()}>
            {busy ? t('assessmentEngine.take.saving') : t('assessmentEngine.hub.addInvites')}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};
